import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, KeyRound, Loader2, Send, X } from "lucide-react";
import { api } from "../../lib/api";
import { trainerAccessNote, trainerCredsFailure, trainerSendFailure, USERNAME_TAKEN_FIX } from "../../lib/limoGroup";

/**
 * A trainer's Logiscool login: send it (again), look at it, reset it.
 * Credentials are read live and held only in this component's state — never
 * in a toast, which is what people screenshot without thinking.
 */
export default function TrainerLimoAccess({ trainer, sandbox, canMutate }) {
  const [busy, setBusy] = useState(null);
  const [creds, setCreds] = useState(null);
  const params = { sandbox };
  const linked = sandbox ? trainer.limo_test_linked : trainer.limo_linked;
  const sentAt = sandbox ? trainer.limo_test_access_sent_at : trainer.limo_access_sent_at;
  const noUsername = sandbox ? trainer.limo_test_no_username : trainer.limo_no_username;

  const run = async (what, fn) => {
    setBusy(what);
    try {
      await fn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Operațiunea nu a reușit.");
    } finally {
      setBusy(null);
    }
  };

  const send = () => run("send", async () => {
    const r = await api.post(`/admin/trainers/${trainer.id}/limo/send-access`, null, { params });
    if (r.data.sent) toast.success(trainerAccessNote(r.data, sandbox));
    else toast.error(trainerSendFailure(r.data));
  });

  const view = () => run("view", async () => {
    const r = await api.get(`/admin/trainers/${trainer.id}/limo/credentials`, { params });
    if (!r.data.ok) { toast.error(trainerCredsFailure(r.data)); return; }
    setCreds(r.data);
  });

  const reset = () => {
    const warn = linked
      ? "Trainerul avea deja cont Logiscool. Resetarea îi schimbă parola contului pe care îl folosește deja. Continui?"
      : "Generezi o parolă nouă pentru trainer? Cea veche nu mai merge; cea nouă pleacă imediat pe email.";
    if (!window.confirm(warn)) return;
    run("reset", async () => {
      const r = await api.post(`/admin/trainers/${trainer.id}/limo/reset-password`, null, { params });
      if (!r.data.ok) { toast.error("Resetarea nu a reușit."); return; }
      setCreds({ username: r.data.username, password: r.data.password, password_changed_by_user: false });
      toast.success(r.data.access_email?.sent ? "Parola nouă a fost trimisă pe email." : "Parola a fost resetată; emailul nu a plecat.");
    });
  };

  const btn = "inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50";
  const spin = (what, Icon) => (busy === what ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />);

  return (
    <div className="mt-1 space-y-1" data-testid={`trainer-access-${trainer.id}`}>
      <div className="text-[11px] text-[#6B7280]">
        {sentAt ? `acces trimis pe ${new Date(sentAt).toLocaleDateString("ro-RO")}` : "acces netrimis"}
        {linked ? " · cont existent" : ""}
      </div>
      {noUsername && (
        <p className="max-w-xs rounded-lg bg-[#FDECEC] px-2 py-1 text-[11px] text-[#9b1f2c]" data-testid={`trainer-no-username-${trainer.id}`}>
          {USERNAME_TAKEN_FIX}
        </p>
      )}
      {canMutate && !noUsername && (
        <div className="flex flex-wrap gap-1">
          <button type="button" onClick={send} disabled={Boolean(busy)} className={btn} data-testid={`trainer-send-access-${trainer.id}`}>
            {spin("send", Send)} {sentAt ? "Retrimite accesul" : "Trimite accesul"}
          </button>
          <button type="button" onClick={view} disabled={Boolean(busy)} className={btn} data-testid={`trainer-view-access-${trainer.id}`}>
            {spin("view", Eye)} Vezi datele
          </button>
          <button type="button" onClick={reset} disabled={Boolean(busy)} className={btn} data-testid={`trainer-reset-${trainer.id}`}>
            {spin("reset", KeyRound)} Resetează parola
          </button>
        </div>
      )}
      {creds && (
        <div className="relative rounded-lg bg-[#F0E7F4] px-2.5 py-1.5 font-mono text-[11px] text-[#373A45]" data-testid={`trainer-creds-${trainer.id}`}>
          <button type="button" onClick={() => setCreds(null)} className="absolute right-1 top-1 text-[#6B7280]" aria-label="Ascunde">
            <X size={11} />
          </button>
          <div>utilizator: <span className="select-all">{creds.username || "—"}</span></div>
          <div>parolă: {creds.password
            ? <span className="select-all">{creds.password}</span>
            : <span className="font-sans text-[#8a6a1a]">schimbată de trainer; resetează-o dacă a uitat-o</span>}</div>
        </div>
      )}
    </div>
  );
}
